import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Empty, FilterGroup, PageHero, SortBar, toggleIn, useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { formatPrice } from '../../shared/data/common';
import { useTransferSearch } from '../../api/transfers';
import {
  durationLabel, isRoundTrip, journeyLabel, parseTransferQuery, passengersLabel, searchProblem, shortPlace,
  toSearchRequest, toTransferParams, transferQueryString, whenLabel,
} from './searchModel';
import { freeCancellation } from './data';

const typeOf = (offer) => offer.vehicle.type || offer.vehicle.label;

/** Link to a cab's details page; the search stays in the URL so the page survives a refresh. */
export const cabPath = (search, offer) => `/transfers/cab?${transferQueryString(search)}&offer=${encodeURIComponent(offer.offerId)}`;

export function VehicleImage({ vehicle, size = 'md' }) {
  const [broken, setBroken] = useState(false);
  return vehicle.imageUrl && !broken
    ? <img className={`cab-img cab-img-${size}`} src={vehicle.imageUrl} alt={vehicle.label} loading="lazy" onError={() => setBroken(true)} />
    : <span className={`transfer-icon ${size === 'sm' ? 'sm' : ''}`}><Icon name="transfers" size={size === 'sm' ? 26 : 34} strokeWidth={1.5} /></span>;
}

function CabCard({ offer, journey, onSelect }) {
  const { vehicle, policies, price } = offer;
  const free = freeCancellation(policies.cancellation);
  return (
    <article className="transfer-card cab-card">
      <VehicleImage vehicle={vehicle} />
      <div className="transfer-info">
        <h3>{vehicle.label}{vehicle.category && vehicle.type && !vehicle.label.includes(vehicle.category) && <span className="tag">{vehicle.category}</span>}</h3>
        {vehicle.similarModels && <span className="muted small">{vehicle.similarModels}</span>}
        <div className="transfer-meta">
          {vehicle.passengerCapacity != null && <span><Icon name="user" size={14} /> {vehicle.passengerCapacity} seats</span>}
          {vehicle.luggageCapacity != null && <span><Icon name="bag" size={14} /> {vehicle.luggageCapacity} bags</span>}
          {policies.waitingTime && <span><Icon name="clock" size={14} /> {policies.waitingTime}</span>}
        </div>
        <ul className="cab-points">
          {free && <li className="text-primary"><Icon name="check" size={13} strokeWidth={2.5} /> {free}</li>}
          {journey.journeyType === 'AirportTransfer' && policies.meetAndGreet && <li><Icon name="user" size={13} /> Meet & greet: {policies.meetAndGreet}</li>}
          {policies.inclusions.slice(0, 2).map((i) => <li key={i}><Icon name="check" size={13} /> {i}</li>)}
        </ul>
      </div>
      <div className="row-price">
        <b className="price big">{formatPrice(price.total)}</b>
        <span className="muted small">{journey.tripType === 'RoundTrip' ? 'Round trip, whole cab' : 'Whole cab, one way'}</span>
        {price.convenienceFee > 0 && <span className="muted small">{formatPrice(price.fare)} fare + {formatPrice(price.convenienceFee)} fee</span>}
        <button type="button" className="btn btn-primary" onClick={onSelect}>Select</button>
      </div>
    </article>
  );
}

export default function TransferList() {
  const q = useQuery();
  const navigate = useNavigate();
  const search = parseTransferQuery(q);
  const problem = searchProblem(search);
  const { loading, data, error, retry } = useTransferSearch(problem ? null : search, toSearchRequest);
  const [sort, setSort] = useState('price');
  const [types, setTypes] = useState([]);

  const offers = useMemo(() => data?.offers || [], [data]);
  const typeList = [...new Set(offers.map(typeOf))];
  const list = offers
    .filter((o) => !types.length || types.includes(typeOf(o)))
    .sort((a, b) => (sort === 'seats'
      ? (b.vehicle.passengerCapacity || 0) - (a.vehicle.passengerCapacity || 0) || a.price.total - b.price.total
      : a.price.total - b.price.total));

  const journey = data?.journey;
  const route = `${shortPlace(search.pickup.name) || 'Pickup'} → ${shortPlace(search.drop.name) || 'Drop'}`;
  const when = `${whenLabel(search.date, search.time)}${isRoundTrip(search) ? ` · back ${whenLabel(search.returnDate, search.returnTime)}` : ''}`;
  const summary = {
    tag: `${journeyLabel(search.journey)}${isRoundTrip(search) ? ' · Round trip' : ''}`,
    items: [
      ['Pickup', search.pickup.name || '—'],
      ['Drop', search.drop.name || '—'],
      ['When', when],
      ['Passengers', passengersLabel(search.passengers)],
    ],
  };

  return (
    <>
      <PageHero tab="transfers" title={`Cabs: ${route}`} query={toTransferParams(search)} summary={summary}
        subtitle={`${journeyLabel(search.journey)} · ${when} · ${passengersLabel(search.passengers)}`} />
      <div className="container listing">
        <aside className="filters">
          <div className="filters-head"><b>Filters</b>
            <button type="button" className="link small" onClick={() => setTypes([])}>Clear all</button>
          </div>
          <FilterGroup title="Cab type">
            {typeList.length ? typeList.map((t) => (
              <Check key={t} label={t} checked={types.includes(t)} onChange={(on) => setTypes(toggleIn(types, t, on))}
                extra={formatPrice(Math.min(...offers.filter((o) => typeOf(o) === t).map((o) => o.price.total)))} />
            )) : <span className="muted small">—</span>}
          </FilterGroup>
          {journey && (
            <FilterGroup title="Your trip">
              <div className="kv-grid one">
                {journey.distance && <div><span className="muted small">Distance</span><b>{journey.distance}</b></div>}
                {journey.durationMinutes > 0 && <div><span className="muted small">Drive time</span><b>about {durationLabel(journey.durationMinutes)}</b></div>}
              </div>
            </FilterGroup>
          )}
          <FilterGroup title="Good to know">
            <ul className="policy-list small-list">
              <li>Prices are for the whole cab and include taxes, tolls and parking unless stated.</li>
              <li>Cabs too small for {passengersLabel(search.passengers)} are not shown.</li>
              <li>Driver details are shared before pickup; track your driver from My Trips.</li>
            </ul>
          </FilterGroup>
        </aside>

        <div className="results">
          {problem && <div className="api-message error" role="alert">{problem} Use <b>Modify Search</b> above.</div>}
          {!problem && loading && (
            <div className="api-message" role="status"><span className="spinner sm" /> Finding cabs for your trip…</div>
          )}
          {!problem && error && (
            <div className="api-message error" role="alert">
              <p>{error}</p>
              <button type="button" className="btn btn-outline btn-sm" onClick={retry}>Try again</button>
            </div>
          )}
          {data?.warnings?.map((w) => <div key={w} className="api-message warning">{w}</div>)}
          {data && (
            <>
              <SortBar count={list.length} label={`cab${list.length === 1 ? '' : 's'}`} sort={sort} setSort={setSort}
                options={[['price', 'Lowest price'], ['seats', 'Most seats']]} />
              {list.length === 0 && (
                <Empty text={offers.length ? 'No cabs match your filters.' : 'No cabs are available for this trip. Try a different time, or check the pickup and drop.'} />
              )}
              <div className="transfer-list">
                {list.map((offer) => (
                  <CabCard key={offer.offerId} offer={offer} journey={data.journey}
                    onSelect={() => navigate(cabPath(search, offer), { state: { offer, journey: data.journey } })} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
